const HOUR_MS = 60 * 60 * 1000;
const PICKUP_WINDOW_MS = 12 * HOUR_MS;

const pad = value => String(value).padStart(2, "0");

export const startOfWeek = date => {
    const weekStart = new Date(date);
    weekStart.setHours(0, 0, 0, 0);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() + (day === 0 ? -6 : 1 - day));
    return weekStart;
};

export const addDays = (date, amount) => {
    const result = new Date(date);
    result.setDate(result.getDate() + amount);
    return result;
};

export const dateKey = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const timestampFor = (date, hour) => {
    const result = new Date(`${date}T00:00:00`);
    result.setHours(Number(hour), 0, 0, 0);
    return result.getTime();
};

export const hourLabel = hour => `${pad(hour)}:00`;

export const hourOptions = (startHour, endHour, includeStart, includeEnd) => {
    const options = [];
    const first = includeStart ? startHour : startHour + 1;
    const last = includeEnd ? endHour : endHour - 1;
    for (let hour = first; hour <= last; hour++) {
        options.push({ "id": String(hour), "label": hourLabel(hour) });
    }
    return options;
};

export const weekDays = (weekStart, showSevenDays) => {
    const numberOfDays = showSevenDays ? 7 : 5;
    return Array.from({ length: numberOfDays }, (_, index) => addDays(weekStart, index));
};

export const displayedHours = fullDay => {
    const startHour = fullDay ? 0 : 7;
    const endHour = fullDay ? 24 : 18;
    return Array.from({ length: endHour - startHour }, (_, index) => startHour + index);
};

export const intervalsOverlap = (firstStart, firstEnd, secondStart, secondEnd) => (
    Number(firstStart) < Number(secondEnd) && Number(firstEnd) > Number(secondStart)
);

export const conflictingBooking = (bookings, start, end, excludedBookingId = null) => Object.values(bookings || {}).find(booking => (
    booking
    && booking.ID !== excludedBookingId
    && booking.Cancelled !== true
    && intervalsOverlap(start, end, booking.Start, booking.End)
));

export const isBookingOwner = (booking, userID) => (
    String(booking?.UserID || "") === String(userID || "")
);

export const isAuthBookingOwner = (booking, authUID) => (
    Boolean(authUID) && booking?.AuthUID === authUID
);

export const canManageBooking = (booking, authUID, now = Date.now()) => (
    (isAuthBookingOwner(booking, authUID) || (Boolean(authUID) && booking?.BookedByAuthUID === authUID))
    && Number(booking?.Start) > now
    && booking?.Mileage?.StartRecorded !== true
);

export const initialMileage = () => ({
    "StartRecorded": false,
    "EndRecorded": false,
    "Complete": false,
});

const mileageState = booking => ({
    startRecorded: booking?.Mileage?.StartRecorded === true,
    endRecorded: booking?.Mileage?.EndRecorded === true,
    complete: booking?.Mileage?.Complete === true,
});

export const mileageCandidates = (bookings, authUID, now = Date.now()) => Object.values(bookings || {})
    .filter(booking => booking && booking.Cancelled !== true && booking.AuthUID === authUID)
    .map(booking => {
        const mileage = mileageState(booking);
        if (mileage.complete || mileage.endRecorded) return null;
        if (mileage.startRecorded) return { booking, stage: "return", priority: 0 };
        if (now >= Number(booking.Start) - PICKUP_WINDOW_MS && now <= Number(booking.End)) {
            return { booking, stage: "pickup", priority: 1 };
        }
        if (now > Number(booking.End)) return { booking, stage: "pickup", priority: 2 };
        return null;
    })
    .filter(Boolean)
    .sort((first, second) => first.priority - second.priority || Number(first.booking.Start) - Number(second.booking.Start));

export const resolveMileageBooking = (bookings, authUID, now = Date.now()) => {
    const candidates = mileageCandidates(bookings, authUID, now);
    if (candidates.length === 0) return { candidates, selected: null, ambiguous: false };
    const topCandidates = candidates.filter(candidate => candidate.priority === candidates[0].priority);
    return {
        candidates,
        selected: topCandidates.length === 1 ? topCandidates[0] : null,
        ambiguous: topCandidates.length > 1,
    };
};

export const latestReturnMileage = (bookings, beforeTimestamp = Infinity) => Object.values(bookings || {})
    .filter(booking => (
        booking?.Cancelled !== true
        && booking?.Mileage?.EndRecorded === true
        && Number.isFinite(Number(booking?.Mileage?.End?.Value))
        && Number(booking.End) <= Number(beforeTimestamp)
    ))
    .sort((first, second) => Number(second.End) - Number(first.End))[0]?.Mileage?.End?.Value ?? null;

export const validateMileage = value => {
    const mileage = Number(value);
    if (!Number.isInteger(mileage) || mileage < 0) {
        throw new Error("Mileage must be a whole number of zero or more.");
    }
    return mileage;
};

export const mileageStatus = booking => {
    if (booking?.Mileage?.ReviewRequired === true) return "Admin review";
    if (booking?.Mileage?.Complete === true) return "Complete";
    if (booking?.Mileage?.StartRecorded === true) return "Return mileage needed";
    return "Pickup mileage needed";
};

export const formatBookingTime = booking => {
    const start = new Date(Number(booking.Start));
    const end = new Date(Number(booking.End));
    const crossesMidnight = end.getDate() !== start.getDate() && end.getHours() === 0;
    return `${hourLabel(start.getHours())}-${crossesMidnight ? "24:00" : hourLabel(end.getHours())}`;
};

export const HOUR_HEIGHT = 64;
export const HOUR_IN_MS = HOUR_MS;
export const PICKUP_WINDOW_IN_MS = PICKUP_WINDOW_MS;
